import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { 
  Sparkles, ShieldCheck, MessageSquare, LogOut, 
  User, Phone, Lock, Save, Bell, AlertTriangle, Loader2,
  Sun, Moon, CheckCircle2, Smartphone, Users, Award, 
  FileText, Layout, Calendar, Zap, Building2, ArrowLeft,
  Tv, Eye, Shield, Laptop, Fingerprint
} from 'lucide-react';
import { ChatInterno } from '../shared/ChatInterno';
import { usePasskeyEnrollment } from '../../hooks/usePasskeyEnrollment';
import { api } from '../../api/client';
import { toast } from 'sonner';

type TabType = 'avisos' | 'chat' | 'oferecemos' | 'publico' | 'dados';

interface NoticeItem {
  id: string;
  title: string;
  content: string;
  time: string;
  icon: 'sparkles' | 'shield' | 'message';
  gradient: string;
}

export function PublicDashboardPage() {
  const { user: rawUser, logout, checkAuth } = useAuthStore();
  const user = rawUser as any;
  const [activeTab, setActiveTab] = useState<TabType>('avisos');
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);

  const { registerPasskey, loading: passkeyLoading, error: passkeyError } = usePasskeyEnrollment();
  const [registeringPasskeyState, setRegisteringPasskeyState] = useState(false);

  const handleRegisterBiometrics = async () => {
    const token = localStorage.getItem('ac_token');
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }
    
    setRegisteringPasskeyState(true);
    try {
      const success = await registerPasskey(token, {
        nomeDispositivo: 'Celular/Dispositivo do Visitante',
        sistemaOp: 'PWA Mobile',
      });
      if (success) {
        toast.success('Biometria cadastrada com sucesso! Agora você poderá acessar usando seu leitor de digital.');
        await checkAuth();
      } else {
        toast.error(passkeyError || 'O cadastro de biometria foi cancelado ou falhou.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Falha ao registrar biometria.');
    } finally {
      setRegisteringPasskeyState(false);
    }
  };
  
  // Theme state defaulting to light
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('visitor_theme') as 'light' | 'dark') || 'light';
  });

  useEffect(() => {
    localStorage.setItem('visitor_theme', theme);
  }, [theme]);

  // Profile edit states
  const [nome, setNome] = useState(user?.nome || '');
  const [telefone, setTelefone] = useState(user?.telefone || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setNome(user.nome || '');
      setTelefone(user.telefone || '');
    }
  }, [user]);

  const handleLogout = async () => {
    const confirm = window.confirm('Deseja realmente sair da sua conta?');
    if (confirm) {
      await logout();
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast.error('O nome completo é obrigatório.');
      return;
    }

    setSavingProfile(true);
    try {
      await api.patch('/auth/me', { 
        nome: nome.trim(),
        telefone: telefone.trim() 
      });
      await checkAuth();
      toast.success('Dados atualizados com sucesso!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao salvar alterações.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error('Informe a nova senha.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('A confirmação da senha não confere.');
      return;
    }

    setSavingPassword(true);
    try {
      await api.patch('/auth/me/password', { 
        password: newPassword 
      });
      toast.success('Senha atualizada com sucesso!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao atualizar senha.');
    } finally {
      setSavingPassword(false);
    }
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const changeTab = (tab: TabType) => {
    setActiveTab(tab);
    setSelectedFeature(null);
  };

  // Color mapping based on theme
  const colors = {
    bg: theme === 'light' ? '#f8fafc' : '#020617',
    headerBg: theme === 'light' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(15, 23, 42, 0.65)',
    border: theme === 'light' ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.08)',
    text: theme === 'light' ? '#0f172a' : '#f8fafc',
    textMuted: theme === 'light' ? '#475569' : '#94a3b8',
    cardBg: theme === 'light' ? '#ffffff' : 'rgba(15, 23, 42, 0.45)',
    cardBorder: theme === 'light' ? 'rgba(148, 163, 184, 0.12)' : 'rgba(255, 255, 255, 0.06)',
    cardShadow: theme === 'light' ? '0 10px 30px rgba(148, 163, 184, 0.06)' : '0 10px 30px rgba(0, 0, 0, 0.25)',
    inputBg: theme === 'light' ? '#ffffff' : 'rgba(2, 6, 23, 0.4)',
    inputBorder: theme === 'light' ? 'rgba(148, 163, 184, 0.25)' : 'rgba(255, 255, 255, 0.1)',
    inputText: theme === 'light' ? '#0f172a' : '#ffffff',
    btnActiveText: theme === 'light' ? '#047857' : '#ffffff',
    btnActiveBg: theme === 'light' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.16)',
    btnActiveBorder: theme === 'light' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(16, 185, 129, 0.3)',
    bottomBarBg: theme === 'light' ? '#ffffff' : '#0f172a',
    bottomBarBorder: theme === 'light' ? 'rgba(148, 163, 184, 0.15)' : 'rgba(255, 255, 255, 0.08)'
  };

  const notices: NoticeItem[] = [
    {
      id: '1',
      title: 'Seja bem-vindo(a) ao Aluno Conectado!',
      content: 'Sua conta de visitante foi ativada com sucesso. Este painel permite que você visualize comunicados do sistema e entre em contato direto com a equipe da escola através da nossa ferramenta de chat.',
      time: 'Agora mesmo',
      icon: 'sparkles',
      gradient: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)'
    },
    {
      id: '2',
      title: 'Ambiente Seguro e Monitorado',
      content: 'Para a segurança de todos, suas ações estão sob a proteção dos nossos protocolos de conformidade digital. O perfil de visitante não expõe informações de estudantes, turmas ou notas acadêmicas.',
      time: '10 minutos atrás',
      icon: 'shield',
      gradient: 'linear-gradient(135deg, #34d399 0%, #059669 100%)'
    },
    {
      id: '3',
      title: 'Atendimento e Suporte em Tempo Real',
      content: 'Precisa tirar alguma dúvida sobre o funcionamento da escola ou agendar uma visita? Vá até a aba "Chat de Suporte" para falar com a recepção e obter atendimento online.',
      time: '25 minutos atrás',
      icon: 'message',
      gradient: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)'
    }
  ];

  const getNoticeIcon = (type: string) => {
    switch (type) {
      case 'shield':
        return <ShieldCheck size={20} color="#fff" />;
      case 'message':
        return <MessageSquare size={20} color="#fff" />;
      default:
        return <Sparkles size={20} color="#fff" />;
    }
  };

  // Details content database for showcase cards click action
  const featureDetails: Record<string, {
    title: string;
    subtitle: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    howItWorks: string[];
    schoolBenefits: string[];
    familyBenefits: string[];
    sandbox: React.ReactNode;
  }> = {
    frequencia: {
      title: 'Frequência Inteligente',
      subtitle: 'Controle de acesso em tempo real e notificações automáticas',
      description: 'Chega de dúvidas sobre a chegada dos alunos à escola. O Aluno Conectado envia alertas imediatos no celular dos pais no exato segundo em que o estudante se identifica na entrada.',
      icon: <CheckCircle2 size={32} color="#ffffff" />,
      color: '#10b981',
      howItWorks: [
        'O estudante passa a carteirinha física ou digital na catraca.',
        'O leitor biométrico/RFID registra a presença no banco de dados.',
        'O servidor processa e envia uma notificação push com foto aos pais.'
      ],
      schoolBenefits: [
        'Redução drástica nas ligações de pais perguntando sobre atrasos.',
        'Relatórios de infrequência gerados automaticamente para a direção.',
        'Facilidade no controle de fluxo na portaria.'
      ],
      familyBenefits: [
        'Paz de espírito sabendo que seu filho chegou em segurança.',
        'Histórico completo de entradas e saídas sempre à mão.',
        'Alertas imediatos em caso de ausência não justificada.'
      ],
      sandbox: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#10b981', letterSpacing: '0.05em' }}>SIMULADOR DE NOTIFICAÇÕES</span>
          <div style={{ background: theme === 'light' ? '#f1f5f9' : '#020617', border: '1px solid rgba(16,185,129,0.2)', padding: 12, borderRadius: 10 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Entrada Registrada</span>
            </div>
            <p style={{ fontSize: '0.75rem', margin: 0, color: colors.textMuted }}>Maria Silva entrou na escola às 07:02:18.</p>
          </div>
          <div style={{ background: theme === 'light' ? '#f1f5f9' : '#020617', border: '1px solid rgba(239,68,68,0.2)', padding: 12, borderRadius: 10 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Saída Registrada</span>
            </div>
            <p style={{ fontSize: '0.75rem', margin: 0, color: colors.textMuted }}>Maria Silva saiu da escola às 12:05:44.</p>
          </div>
        </div>
      )
    },
    boletim: {
      title: 'Boletim & Notas Online',
      subtitle: 'Histórico pedagógico completo e gráficos de desempenho',
      description: 'Substitua o papel pelo acompanhamento de notas contínuo. Pais e alunos visualizam boletins de notas, médias bimestrais e observações de professores na hora.',
      icon: <FileText size={32} color="#ffffff" />,
      color: '#6366f1',
      howItWorks: [
        'O professor lança as notas e frequências no diário de classe digital.',
        'A média da disciplina é calculada automaticamente conforme a fórmula escolar.',
        'O boletim é atualizado em tempo real na tela do celular do aluno e do pai.'
      ],
      schoolBenefits: [
        'Fim da impressão e re-impressão de boletins de papel.',
        'Cálculo automático de recuperação e exames finais.',
        'Visualização imediata de alunos com risco de reprovação.'
      ],
      familyBenefits: [
        'Acompanhamento de notas ao longo do bimestre, sem surpresas no final.',
        'Detecção de dificuldades de aprendizagem nas primeiras semanas.',
        'Visualização de comentários pedagógicos dos professores.'
      ],
      sandbox: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6366f1', letterSpacing: '0.05em' }}>MOCK DESEMPENHO BIMESTRAL</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
                <span>Matemática</span>
                <strong>8.5 / 10</strong>
              </div>
              <div style={{ height: 6, background: theme === 'light' ? '#e2e8f0' : '#1e293b', borderRadius: 3 }}>
                <div style={{ width: '85%', height: '100%', background: '#6366f1', borderRadius: 3 }} />
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
                <span>Português</span>
                <strong>9.2 / 10</strong>
              </div>
              <div style={{ height: 6, background: theme === 'light' ? '#e2e8f0' : '#1e293b', borderRadius: 3 }}>
                <div style={{ width: '92%', height: '100%', background: '#10b981', borderRadius: 3 }} />
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
                <span>História</span>
                <strong>6.0 / 10</strong>
              </div>
              <div style={{ height: 6, background: theme === 'light' ? '#e2e8f0' : '#1e293b', borderRadius: 3 }}>
                <div style={{ width: '60%', height: '100%', background: '#ef4444', borderRadius: 3 }} />
              </div>
            </div>
          </div>
        </div>
      )
    },
    chat: {
      title: 'Comunicação Direta',
      subtitle: 'Diálogo oficial e seguro entre a escola e a família',
      description: 'Centralize conversas de forma institucional. Pais e professores conversam em um ambiente profissional, mantendo privacidade de contatos pessoais.',
      icon: <MessageSquare size={32} color="#ffffff" />,
      color: '#3b82f6',
      howItWorks: [
        'A escola define quem pode iniciar chats (ex: professores e pais).',
        'As conversas ficam salvas de forma auditável no sistema.',
        'Sem necessidade de revelar o número de WhatsApp pessoal de docentes.'
      ],
      schoolBenefits: [
        'Privacidade garantida para o corpo docente fora do expediente.',
        'Histórico de conversas disponível para a coordenação em caso de conflitos.',
        'Redução do uso de grupos paralelos não oficiais.'
      ],
      familyBenefits: [
        'Fácil acesso aos professores e equipe administrativa.',
        'Respostas institucionais claras e organizadas.',
        'Notificações no celular quando há novas respostas.'
      ],
      sandbox: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.05em' }}>MURAL DE CHAT</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8, background: theme === 'light' ? '#f1f5f9' : '#020617', borderRadius: 10 }}>
            <div style={{ alignSelf: 'flex-start', background: '#e2e8f0', color: '#0f172a', padding: '6px 10px', borderRadius: '10px 10px 10px 0', fontSize: '0.72rem', maxWidth: '85%' }}>
              Olá! A feira de ciências foi remarcada para sexta-feira?
            </div>
            <div style={{ alignSelf: 'flex-end', background: '#3b82f6', color: '#ffffff', padding: '6px 10px', borderRadius: '10px 10px 0 10px', fontSize: '0.72rem', maxWidth: '85%' }}>
              Sim, isso mesmo! Será na sexta-feira às 14:00. Esperamos vocês!
            </div>
          </div>
        </div>
      )
    },
    seguranca: {
      title: 'Proteção & Passkeys',
      subtitle: 'Segurança cibernética máxima e conformidade com a LGPD',
      description: 'Garantimos a proteção dos dados dos alunos com criptografia e validações robustas de novos acessos de forma a cumprir a LGPD.',
      icon: <ShieldCheck size={32} color="#ffffff" />,
      color: '#8b5cf6',
      howItWorks: [
        'Ative biometria/passkey para autenticar sem necessidade de senhas.',
        'Novos dispositivos passam por uma validação QR Code em celular autorizado.',
        'Telas sensíveis bloqueiam a exibição de fotos caso o dispositivo não seja confiável.'
      ],
      schoolBenefits: [
        'Cumprimento integral das obrigações da Lei Geral de Proteção de Dados.',
        'Impossibilidade de clonagem de contas de alunos.',
        'Zero vazamentos de boletins e endereços.'
      ],
      familyBenefits: [
        'Certeza de que apenas pessoas autorizadas veem fotos e notas do aluno.',
        'Login fácil e sem senhas complicadas usando biometria do próprio aparelho.',
        'Notificação em tempo real sobre qualquer tentativa de acesso estranho.'
      ],
      sandbox: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#8b5cf6', letterSpacing: '0.05em', alignSelf: 'flex-start' }}>STATUS DE SEGURANÇA</span>
          <div style={{ 
            width: 50, height: 50, borderRadius: '50%', border: '3px solid #8b5cf6', 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'visitor-pulse 2s infinite ease-in-out'
          }}>
            <ShieldCheck size={24} color="#8b5cf6" />
          </div>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8b5cf6' }}>Dispositivo Protegido</span>
          <p style={{ fontSize: '0.65rem', margin: 0, textAlign: 'center', color: colors.textMuted }}>Biometria ativada e verificação de hardware em conformidade.</p>
        </div>
      )
    },
    tv: {
      title: 'TV Digital e Mídias',
      subtitle: 'Murais eletrônicos inteligentes e feed escolar interativo',
      description: 'Sincronize as mídias da escola! Murais digitais dinâmicos exibem comunicados, avisos importantes e atividades dos alunos.',
      icon: <Layout size={32} color="#ffffff" />,
      color: '#ec4899',
      howItWorks: [
        'A coordenação faz upload das fotos e escolhe quais turmas marcar.',
        'As TVs da escola sincronizam e criam slideshows automáticos nas salas e corredores.',
        'O app exibe as publicações no feed de cada família.'
      ],
      schoolBenefits: [
        'Fim dos murais de papel rasgados ou desatualizados.',
        'Ambiente visualmente moderno que chama a atenção do aluno.',
        'Melhoria na percepção da marca da escola.'
      ],
      familyBenefits: [
        'Visibilidade do dia a dia dos alunos dentro da escola.',
        'Acesso aos cartazes digitais de eventos com datas.',
        'Fotos e atividades pedagógicas salvas no histórico.'
      ],
      sandbox: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#ec4899', letterSpacing: '0.05em' }}>MURAL DA TV ESCOLA</span>
          <div style={{ background: '#ec4899', height: 70, borderRadius: 8, overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
            <div style={{ position: 'absolute', top: 5, left: 5, fontSize: '0.6rem', background: 'rgba(0,0,0,0.5)', padding: '2px 4px', borderRadius: 4 }}>TV PORTARIA</div>
            <Tv size={24} style={{ opacity: 0.8, marginRight: 6 }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Projeto Feira de Ciências 2026</span>
          </div>
        </div>
      )
    },
    pwa: {
      title: 'Aplicativo PWA Instalável',
      subtitle: 'Instalação instantânea na tela inicial sem precisar de App Store',
      description: 'O Aluno Conectado utiliza a tecnologia de Progressive Web App, rodando diretamente do navegador sem ocupar o armazenamento do celular.',
      icon: <Smartphone size={32} color="#ffffff" />,
      color: '#f59e0b',
      howItWorks: [
        'Acesse o site pelo link no navegador do celular.',
        'Toque em "Instalar aplicativo" ou no ícone de compartilhamento.',
        'O ícone da escola surge na tela inicial com experiência de tela cheia.'
      ],
      schoolBenefits: [
        'Não depende das aprovações demoradas da Apple/Google Play Store.',
        'Custo zero de distribuição de aplicativo.',
        'Atualização instantânea de novas versões.'
      ],
      familyBenefits: [
        'Consome menos de 2MB de armazenamento.',
        'Inicialização ultra-rápida mesmo em conexões lentas (3G/4G).',
        'Notificações nativas na tela de bloqueio do celular.'
      ],
      sandbox: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#f59e0b', letterSpacing: '0.05em', alignSelf: 'flex-start' }}>TELA DE INSTALAÇÃO</span>
          <div style={{ background: theme === 'light' ? '#f1f5f9' : '#0f172a', border: '1px solid rgba(245,158,11,0.3)', padding: 10, borderRadius: 8, display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Smartphone size={14} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 750 }}>Adicionar à Tela Inicial</div>
              <div style={{ fontSize: '0.62rem', color: colors.textMuted }}>Instale em segundos.</div>
            </div>
            <button style={{ border: 'none', background: '#f59e0b', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '4px 8px', borderRadius: 6 }}>Instalar</button>
          </div>
        </div>
      )
    }
  };

  const currentFeature = selectedFeature ? featureDetails[selectedFeature] : null;

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.bg, 
      color: colors.text, 
      display: 'flex', 
      flexDirection: 'column',
      transition: 'background 0.3s ease, color 0.3s ease'
    }}>
      
      {/* Floating Glass Header */}
      <header className="visitor-floating-header" style={{ 
        height: 60, 
        border: `1px solid ${colors.border}`, 
        background: colors.headerBg, 
        backdropFilter: 'blur(16px)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '0 20px',
        position: 'sticky',
        top: 12,
        zIndex: 100,
        maxWidth: 1000,
        margin: '12px auto',
        width: 'calc(100% - 32px)',
        borderRadius: 16,
        boxShadow: colors.cardShadow,
        transition: 'all 0.3s ease, top 0.3s'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ 
            width: 28, height: 28, borderRadius: 8, 
            background: 'linear-gradient(135deg, #10b981, #059669)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            boxShadow: '0 0 12px rgba(16,185,129,0.3)' 
          }}>
            <Sparkles size={14} color="#fff" />
          </div>
          <span style={{ fontSize: '0.98rem', fontWeight: 800, color: colors.text, letterSpacing: '-0.02em' }}>Visitante Conectado</span>
        </div>

        {/* Desktop Navigation Tabs */}
        <nav className="visitor-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button 
            onClick={() => changeTab('avisos')}
            className={`visitor-tab-button ${activeTab === 'avisos' ? 'active' : ''}`}
          >
            <Bell size={14} />
            <span>Avisos</span>
          </button>
          <button 
            onClick={() => changeTab('oferecemos')}
            className={`visitor-tab-button ${activeTab === 'oferecemos' ? 'active' : ''}`}
          >
            <Zap size={14} />
            <span>O que Oferecemos</span>
          </button>
          <button 
            onClick={() => changeTab('publico')}
            className={`visitor-tab-button ${activeTab === 'publico' ? 'active' : ''}`}
          >
            <Users size={14} />
            <span>Público & Objetivos</span>
          </button>
          <button 
            onClick={() => changeTab('chat')}
            className={`visitor-tab-button ${activeTab === 'chat' ? 'active' : ''}`}
          >
            <MessageSquare size={14} />
            <span>Chat de Suporte</span>
          </button>
          <button 
            onClick={() => changeTab('dados')}
            className={`visitor-tab-button ${activeTab === 'dados' ? 'active' : ''}`}
          >
            <User size={14} />
            <span>Meus Dados</span>
          </button>
        </nav>

        {/* Right Header Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Light/Dark Toggle */}
          <button 
            onClick={toggleTheme}
            style={{
              width: 32, height: 32, borderRadius: 10, border: `1px solid ${colors.border}`,
              background: colors.cardBg, color: colors.text, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', outline: 'none'
            }}
            title={theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}
          >
            {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
          </button>

          {/* User Name */}
          <span className="visitor-header-name" style={{ fontSize: '0.82rem', fontWeight: 650, color: colors.textMuted, display: 'inline-block' }}>
            Olá, <strong style={{ color: colors.text }}>{user?.nome?.split(' ')[0] || 'Visitante'}</strong>
          </span>

          {/* Logout */}
          <button 
            onClick={handleLogout}
            style={{
              width: 32, height: 32, borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)',
              background: 'rgba(239,68,68,0.06)', color: '#f87171', display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', outline: 'none'
            }}
            title="Sair da Conta"
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; e.currentTarget.style.color = '#f87171'; }}
          >
            <LogOut size={14} />
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="visitor-main-container" style={{ flex: 1, padding: '16px 20px 40px 20px', maxWidth: 1000, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        
        {/* If a feature card is selected, bypass tabs and render detailed subpage */}
        {activeTab === 'oferecemos' && selectedFeature && currentFeature ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'visitor-fade-in 0.2s ease-out both' }}>
            
            {/* Back Button */}
            <div>
              <button 
                onClick={() => setSelectedFeature(null)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                  borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.cardBg,
                  color: colors.text, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.2s', outline: 'none'
                }}
                onMouseEnter={e => e.currentTarget.style.background = theme === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'}
                onMouseLeave={e => e.currentTarget.style.background = colors.cardBg}
              >
                <ArrowLeft size={16} />
                <span>Voltar para Recursos</span>
              </button>
            </div>

            {/* Layout Split: Left Info, Right Sandbox Mockup */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
              
              {/* Left Column: Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ 
                    width: 50, height: 50, borderRadius: 14, 
                    background: currentFeature.color, 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                  }}>
                    {currentFeature.icon}
                  </div>
                  <div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: colors.text }}>{currentFeature.title}</h2>
                    <span style={{ fontSize: '0.85rem', color: colors.textMuted, fontWeight: 500 }}>{currentFeature.subtitle}</span>
                  </div>
                </div>

                <p style={{ fontSize: '0.88rem', color: colors.text, lineHeight: 1.6, margin: 0 }}>
                  {currentFeature.description}
                </p>

                {/* Grid of details */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Como funciona */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 18, boxShadow: colors.cardShadow }}>
                    <h3 style={{ fontSize: '0.88rem', fontWeight: 750, margin: '0 0 10px 0', color: currentFeature.color }}>🛠️ Como Funciona</h3>
                    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: colors.textMuted, lineHeight: 1.4 }}>
                      {currentFeature.howItWorks.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Beneficios Escola */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 18, boxShadow: colors.cardShadow }}>
                    <h3 style={{ fontSize: '0.88rem', fontWeight: 750, margin: '0 0 10px 0', color: '#10b981' }}>🏫 Benefícios para a Escola</h3>
                    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: colors.textMuted, lineHeight: 1.4 }}>
                      {currentFeature.schoolBenefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Beneficios Familia */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 18, boxShadow: colors.cardShadow }}>
                    <h3 style={{ fontSize: '0.88rem', fontWeight: 750, margin: '0 0 10px 0', color: '#3b82f6' }}>🏠 Benefícios para a Família</h3>
                    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: colors.textMuted, lineHeight: 1.4 }}>
                      {currentFeature.familyBenefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right Column: Visual Sandbox Previews */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ 
                  background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, 
                  borderRadius: 20, padding: 22, boxShadow: colors.cardShadow,
                  display: 'flex', flexDirection: 'column', gap: 16, 
                  position: 'sticky', top: 86
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${colors.border}`, paddingBottom: 10 }}>
                    <Eye size={16} style={{ color: currentFeature.color }} />
                    <span style={{ fontSize: '0.78rem', fontWeight: 750, color: colors.text, textTransform: 'uppercase', letterSpacing: '0.05em' }}>MOCK INTERATIVO DO PRODUTO</span>
                  </div>

                  <div style={{ 
                    background: theme === 'light' ? '#f8fafc' : 'rgba(2,6,23,0.3)', 
                    border: `1px solid ${colors.border}`, 
                    borderRadius: 14, 
                    padding: 16,
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {currentFeature.sandbox}
                  </div>

                  <div style={{ fontSize: '0.72rem', color: colors.textMuted, lineHeight: 1.4, margin: 0, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
                    <span>Esta é uma simulação gráfica projetada para fins demonstrativos das APIs internas da PWA.</span>
                  </div>
                </div>
              </div>

            </div>

          </div>
        ) : (
          /* Normal Tab Content */
          <>
            {/* Tab 1: Avisos */}
            {activeTab === 'avisos' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, animation: 'visitor-fade-in 0.2s ease-out both' }}>
                <div style={{ marginBottom: 4 }}>
                  <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 6px 0', color: colors.text }}>📢 Quadro de Avisos</h2>
                  <p style={{ fontSize: '0.85rem', color: colors.textMuted, margin: 0 }}>Fique por dentro das últimas atualizações e comunicados oficiais da escola.</p>
                </div>

                {notices.map((notice) => (
                  <div 
                    key={notice.id} 
                    style={{ 
                      background: colors.cardBg, 
                      border: `1px solid ${colors.cardBorder}`, 
                      borderRadius: 16, 
                      padding: 20,
                      display: 'flex',
                      gap: 16,
                      alignItems: 'flex-start',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: colors.cardShadow,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ 
                      width: 40, height: 40, borderRadius: 12, 
                      background: notice.gradient, 
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                    }}>
                      {getNoticeIcon(notice.icon)}
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '0.98rem', fontWeight: 750, color: colors.text, margin: 0 }}>{notice.title}</h3>
                        <span style={{ fontSize: '0.74rem', color: colors.textMuted, fontWeight: 600 }}>{notice.time}</span>
                      </div>
                      <p style={{ fontSize: '0.84rem', color: colors.textMuted, lineHeight: 1.5, margin: 0 }}>
                        {notice.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Tab 2: O Que Oferecemos Grid */}
            {activeTab === 'oferecemos' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, animation: 'visitor-fade-in 0.2s ease-out both' }}>
                <div>
                  <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 6px 0', color: colors.text }}>💼 O que Oferecemos</h2>
                  <p style={{ fontSize: '0.85rem', color: colors.textMuted, margin: 0 }}>Uma plataforma educacional completa desenvolvida com foco em segurança, comunicação e agilidade. Clique nos cards abaixo para ver detalhes interativos.</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
                  {/* Card 1: Frequencia */}
                  <div 
                    onClick={() => setSelectedFeature('frequencia')}
                    className="visitor-interactive-card"
                    style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <CheckCircle2 size={18} color="#10b981" />
                    </div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 750, margin: '0 0 8px 0', color: colors.text }}>Frequência Inteligente</h3>
                    <p style={{ fontSize: '0.82rem', color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                      Controle automatizado de presença via aplicativo com envio imediato de alertas para os responsáveis quando o aluno entra ou sai da instituição.
                    </p>
                  </div>

                  {/* Card 2: Boletim */}
                  <div 
                    onClick={() => setSelectedFeature('boletim')}
                    className="visitor-interactive-card"
                    style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(99, 102, 241, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <FileText size={18} color="#6366f1" />
                    </div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 750, margin: '0 0 8px 0', color: colors.text }}>Boletim & Notas</h3>
                    <p style={{ fontSize: '0.82rem', color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                      Acompanhamento pedagógico de notas, avaliações agendadas, relatórios de desempenho e observações dos professores em tempo real.
                    </p>
                  </div>

                  {/* Card 3: Chat */}
                  <div 
                    onClick={() => setSelectedFeature('chat')}
                    className="visitor-interactive-card"
                    style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(59, 130, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <MessageSquare size={18} color="#3b82f6" />
                    </div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 750, margin: '0 0 8px 0', color: colors.text }}>Comunicação Direta</h3>
                    <p style={{ fontSize: '0.82rem', color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                      Bate-papo interno integrado e quadro de comunicados para manter pais, professores e coordenação em perfeita sincronia com segurança de dados.
                    </p>
                  </div>

                  {/* Card 4: Seguranca */}
                  <div 
                    onClick={() => setSelectedFeature('seguranca')}
                    className="visitor-interactive-card"
                    style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139, 92, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <ShieldCheck size={18} color="#8b5cf6" />
                    </div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 750, margin: '0 0 8px 0', color: colors.text }}>Proteção & Passkeys</h3>
                    <p style={{ fontSize: '0.82rem', color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                      Segurança cibernética robusta com autenticação por biometria (Passkeys) e autorização de dispositivos para proteger dados de menores.
                    </p>
                  </div>

                  {/* Card 5: TV */}
                  <div 
                    onClick={() => setSelectedFeature('tv')}
                    className="visitor-interactive-card"
                    style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(236, 72, 153, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <Layout size={18} color="#ec4899" />
                    </div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 750, margin: '0 0 8px 0', color: colors.text }}>TV Digital e Mídias</h3>
                    <p style={{ fontSize: '0.82rem', color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                      Gestão centralizada de murais eletrônicos digitais e streaming de fotos ou atividades realizadas nas salas de aula de toda a rede de ensino.
                    </p>
                  </div>

                  {/* Card 6: PWA */}
                  <div 
                    onClick={() => setSelectedFeature('pwa')}
                    className="visitor-interactive-card"
                    style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245, 158, 11, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <Smartphone size={18} color="#f59e0b" />
                    </div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 750, margin: '0 0 8px 0', color: colors.text }}>App PWA Instalável</h3>
                    <p style={{ fontSize: '0.82rem', color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                      Instalação instantânea sem consumir memória do celular, suporte offline parcial e envio de notificações importantes no sistema operacional.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Para Quem & Objetivos */}
            {activeTab === 'publico' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, animation: 'visitor-fade-in 0.2s ease-out both' }}>
                <div>
                  <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 6px 0', color: colors.text }}>🎯 Objetivos & Público-Alvo</h2>
                  <p style={{ fontSize: '0.85rem', color: colors.textMuted, margin: 0 }}>O Aluno Conectado foi desenhado sob medida para suprir as dores de cada participante do ecossistema escolar.</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  {/* Pais */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Users size={16} color="#10b981" />
                      <span style={{ fontSize: '0.8rem', fontWeight: 750, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pais e Mães</span>
                    </div>
                    <h3 style={{ fontSize: '0.94rem', fontWeight: 750, color: colors.text, margin: 0 }}>Segurança e Presença</h3>
                    <p style={{ fontSize: '0.8rem', color: colors.textMuted, margin: 0, lineHeight: 1.4 }}>
                      Tranquilidade ao saber exatamente onde os filhos estão e facilidade no acompanhamento escolar diário sem depender de bilhetes de papel.
                    </p>
                  </div>

                  {/* Alunos */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Award size={16} color="#6366f1" />
                      <span style={{ fontSize: '0.8rem', fontWeight: 750, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Estudantes</span>
                    </div>
                    <h3 style={{ fontSize: '0.94rem', fontWeight: 750, color: colors.text, margin: 0 }}>Autonomia e Foco</h3>
                    <p style={{ fontSize: '0.8rem', color: colors.textMuted, margin: 0, lineHeight: 1.4 }}>
                      Acesso rápido a horários de aulas, tarefas a entregar, datas de exames e notas bimestrais organizadas de forma limpa no celular.
                    </p>
                  </div>

                  {/* Professores */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Calendar size={16} color="#3b82f6" />
                      <span style={{ fontSize: '0.8rem', fontWeight: 750, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Professores</span>
                    </div>
                    <h3 style={{ fontSize: '0.94rem', fontWeight: 750, color: colors.text, margin: 0 }}>Produtividade</h3>
                    <p style={{ fontSize: '0.8rem', color: colors.textMuted, margin: 0, lineHeight: 1.4 }}>
                      Eliminação de diários físicos de chamada. Lançamento rápido de presenças, notas e envio simplificado de avisos escolares à coordenação.
                    </p>
                  </div>

                  {/* Gestores */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 16, padding: 20, boxShadow: colors.cardShadow, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Building2 size={16} color="#8b5cf6" />
                      <span style={{ fontSize: '0.8rem', fontWeight: 750, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Secretarias</span>
                    </div>
                    <h3 style={{ fontSize: '0.94rem', fontWeight: 750, color: colors.text, margin: 0 }}>Gestão e Dados</h3>
                    <p style={{ fontSize: '0.8rem', color: colors.textMuted, margin: 0, lineHeight: 1.4 }}>
                      Auditoria de infrequência escolar em tempo real, dados de evasão unificados e inteligência de dados consolidados para a rede pública.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 4: Chat de Suporte */}
            {activeTab === 'chat' && (
              <div style={{ 
                height: 'calc(100vh - 120px)',
                minHeight: 480,
                display: 'flex', 
                flexDirection: 'column', 
                animation: 'visitor-fade-in 0.2s ease-out both',
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: colors.cardShadow,
                transition: 'all 0.3s ease'
              }}>
                <ChatInterno />
              </div>
            )}

            {/* Tab 5: Meus Dados */}
            {activeTab === 'dados' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, animation: 'visitor-fade-in 0.2s ease-out both' }}>
                <div style={{ marginBottom: 4 }}>
                  <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 6px 0', color: colors.text }}>⚙️ Meus Dados</h2>
                  <p style={{ fontSize: '0.85rem', color: colors.textMuted, margin: 0 }}>Gerencie suas informações pessoais e credenciais de acesso.</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                  
                  {/* Card Dados Pessoais */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 20, padding: 22, boxShadow: colors.cardShadow }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                      <User size={18} style={{ color: '#10b981' }} />
                      <h3 style={{ fontSize: '1rem', fontWeight: 750, color: colors.text, margin: 0 }}>Informações Cadastrais</h3>
                    </div>

                    <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 700, color: colors.textMuted }}>NOME COMPLETO</label>
                        <input 
                          type="text"
                          value={nome}
                          onChange={e => setNome(e.target.value)}
                          placeholder="Seu nome completo"
                          style={{
                            padding: '10px 14px', borderRadius: 10, border: `1px solid ${colors.inputBorder}`,
                            background: colors.inputBg, color: colors.inputText, fontSize: '0.85rem', outline: 'none'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 700, color: colors.textMuted }}>E-MAIL (APENAS LEITURA)</label>
                        <input 
                          type="email"
                          value={user?.email || ''}
                          disabled
                          style={{
                            padding: '10px 14px', borderRadius: 10, border: `1px solid ${colors.inputBorder}`,
                            background: theme === 'light' ? '#e2e8f0' : 'rgba(255,255,255,0.02)', 
                            color: theme === 'light' ? '#64748b' : '#64748b', fontSize: '0.85rem', 
                            cursor: 'not-allowed', outline: 'none'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 700, color: colors.textMuted }}>TELEFONE</label>
                        <input 
                          type="text"
                          value={telefone}
                          onChange={e => setTelefone(e.target.value)}
                          placeholder="(00) 00000-0000"
                          style={{
                            padding: '10px 14px', borderRadius: 10, border: `1px solid ${colors.inputBorder}`,
                            background: colors.inputBg, color: colors.inputText, fontSize: '0.85rem', outline: 'none'
                          }}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={savingProfile}
                        style={{
                          marginTop: 8, padding: '11px 16px', borderRadius: 10, border: 'none',
                          background: savingProfile ? '#065f46' : '#10b981', color: '#ffffff', fontSize: '0.86rem',
                          fontWeight: 700, cursor: savingProfile ? 'wait' : 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => { if (!savingProfile) e.currentTarget.style.background = '#059669'; }}
                        onMouseLeave={e => { if (!savingProfile) e.currentTarget.style.background = '#10b981'; }}
                      >
                        {savingProfile ? (
                          <>
                            <Loader2 size={16} className="animate-spin" style={{ animation: 'lk-spin 1s linear infinite' }} />
                            <span>Salvando...</span>
                          </>
                        ) : (
                          <>
                            <Save size={16} />
                            <span>Salvar Alterações</span>
                          </>
                        )}
                      </button>
                    </form>
                  </div>

                  {/* Card Alterar Senha */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 20, padding: 22, boxShadow: colors.cardShadow }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                      <Lock size={18} style={{ color: '#8b5cf6' }} />
                      <h3 style={{ fontSize: '1rem', fontWeight: 750, color: colors.text, margin: 0 }}>Alterar Senha</h3>
                    </div>

                    <form onSubmit={handleUpdatePassword} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 700, color: colors.textMuted }}>NOVA SENHA</label>
                        <input 
                          type="password"
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          placeholder="Mínimo 6 caracteres"
                          style={{
                            padding: '10px 14px', borderRadius: 10, border: `1px solid ${colors.inputBorder}`,
                            background: colors.inputBg, color: colors.inputText, fontSize: '0.85rem', outline: 'none'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 700, color: colors.textMuted }}>CONFIRMAR NOVA SENHA</label>
                        <input 
                          type="password"
                          value={confirmPassword}
                          onChange={e => setConfirmPassword(e.target.value)}
                          placeholder="Repita a nova senha"
                          style={{
                            padding: '10px 14px', borderRadius: 10, border: `1px solid ${colors.inputBorder}`,
                            background: colors.inputBg, color: colors.inputText, fontSize: '0.85rem', outline: 'none'
                          }}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={savingPassword}
                        style={{
                          marginTop: 8, padding: '11px 16px', borderRadius: 10, border: 'none',
                          background: savingPassword ? '#5b21b6' : '#8b5cf6', color: '#ffffff', fontSize: '0.86rem',
                          fontWeight: 700, cursor: savingPassword ? 'wait' : 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => { if (!savingPassword) e.currentTarget.style.background = '#7c3aed'; }}
                        onMouseLeave={e => { if (!savingPassword) e.currentTarget.style.background = '#8b5cf6'; }}
                      >
                        {savingPassword ? (
                          <>
                            <Loader2 size={16} className="animate-spin" style={{ animation: 'lk-spin 1s linear infinite' }} />
                            <span>Alterando...</span>
                          </>
                        ) : (
                          <>
                            <Save size={16} />
                            <span>Atualizar Senha</span>
                          </>
                        )}
                      </button>
                    </form>
                  </div>

                  {/* Card Segurança Biométrica */}
                  <div style={{ background: colors.cardBg, border: `1px solid ${colors.cardBorder}`, borderRadius: 20, padding: 22, boxShadow: colors.cardShadow }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                      <Fingerprint size={18} style={{ color: '#ec4899' }} />
                      <h3 style={{ fontSize: '1rem', fontWeight: 750, color: colors.text, margin: 0 }}>Acesso Digital</h3>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, background: user?.biometriaAtiva ? 'rgba(16,185,129,0.08)' : 'rgba(148,163,184,0.08)', border: `1px solid ${user?.biometriaAtiva ? 'rgba(16,185,129,0.15)' : 'rgba(148,163,184,0.15)'}` }}>
                        <div style={{ 
                          width: 8, height: 8, borderRadius: '50%', 
                          background: user?.biometriaAtiva ? '#10b981' : '#94a3b8',
                          boxShadow: user?.biometriaAtiva ? '0 0 8px #10b981' : 'none'
                        }} />
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: user?.biometriaAtiva ? '#10b981' : colors.textMuted }}>
                          {user?.biometriaAtiva ? 'BIOMETRIA ATIVADA' : 'BIOMETRIA NÃO ATIVA'}
                        </span>
                      </div>

                      <p style={{ fontSize: '0.8rem', color: colors.textMuted, lineHeight: 1.4, margin: 0 }}>
                        Ativando a biometria/digital, você poderá acessar sua conta instantaneamente nas próximas visitas ao abrir o PWA.
                      </p>

                      <button
                        onClick={handleRegisterBiometrics}
                        disabled={registeringPasskeyState || passkeyLoading}
                        style={{
                          marginTop: 8, padding: '11px 16px', borderRadius: 10, border: 'none',
                          background: user?.biometriaAtiva ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ec4899 0%, #d946ef 100%)',
                          color: '#ffffff', fontSize: '0.86rem', fontWeight: 700,
                          cursor: (registeringPasskeyState || passkeyLoading) ? 'wait' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          boxShadow: user?.biometriaAtiva ? '0 4px 12px rgba(16,185,129,0.2)' : '0 4px 12px rgba(236,72,153,0.2)'
                        }}
                      >
                        {registeringPasskeyState || passkeyLoading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" style={{ animation: 'lk-spin 1s linear infinite' }} />
                            <span>Processando...</span>
                          </>
                        ) : (
                          <>
                            <Fingerprint size={16} />
                            <span>{user?.biometriaAtiva ? 'Cadastrar outro dispositivo' : 'Configurar Biometria'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            )}
          </>
        )}

      </main>

      {/* Mobile Sticky Bottom Navigation Bar */}
      <nav className="visitor-mobile-nav" style={{
        display: 'none',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 64,
        background: colors.bottomBarBg,
        borderTop: `1px solid ${colors.bottomBarBorder}`,
        justifyContent: 'space-around',
        alignItems: 'center',
        zIndex: 100,
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxShadow: theme === 'light' ? '0 -4px 15px rgba(148,163,184,0.06)' : '0 -10px 30px rgba(0,0,0,0.3)',
        transition: 'background 0.3s ease, border-color 0.3s ease'
      }}>
        <button 
          onClick={() => changeTab('avisos')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'avisos' ? '#10b981' : colors.textMuted,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer',
            fontSize: '0.68rem', fontWeight: activeTab === 'avisos' ? 750 : 500, outline: 'none'
          }}
        >
          <Bell size={20} />
          <span>Avisos</span>
        </button>
        <button 
          onClick={() => changeTab('oferecemos')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'oferecemos' ? '#10b981' : colors.textMuted,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer',
            fontSize: '0.68rem', fontWeight: activeTab === 'oferecemos' ? 750 : 500, outline: 'none'
          }}
        >
          <Zap size={20} />
          <span>Recursos</span>
        </button>
        <button 
          onClick={() => changeTab('publico')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'publico' ? '#10b981' : colors.textMuted,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer',
            fontSize: '0.68rem', fontWeight: activeTab === 'publico' ? 750 : 500, outline: 'none'
          }}
        >
          <Users size={20} />
          <span>Objetivos</span>
        </button>
        <button 
          onClick={() => changeTab('chat')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'chat' ? '#10b981' : colors.textMuted,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer',
            fontSize: '0.68rem', fontWeight: activeTab === 'chat' ? 750 : 500, outline: 'none'
          }}
        >
          <MessageSquare size={20} />
          <span>Chat</span>
        </button>
        <button 
          onClick={() => changeTab('dados')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'dados' ? '#10b981' : colors.textMuted,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer',
            fontSize: '0.68rem', fontWeight: activeTab === 'dados' ? 750 : 500, outline: 'none'
          }}
        >
          <User size={20} />
          <span>Dados</span>
        </button>
      </nav>

      {/* Global CSS Inject */}
      <style>{`
        .visitor-tab-button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 9999px;
          border: 1px solid transparent;
          color: ${colors.textMuted};
          background: transparent;
          font-size: 0.85rem;
          font-weight: 650;
          cursor: pointer;
          transition: all 0.2s ease;
          outline: none;
        }
        .visitor-tab-button.active {
          color: ${colors.btnActiveText};
          background: ${colors.btnActiveBg};
          border-color: ${colors.btnActiveBorder};
        }
        .visitor-tab-button:hover:not(.active) {
          color: ${colors.text};
          background: ${theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)'};
        }
        
        .visitor-interactive-card {
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid ${colors.cardBorder};
        }
        .visitor-interactive-card:hover {
          transform: translateY(-4px);
          border-color: rgba(16, 185, 129, 0.4) !important;
          box-shadow: 0 12px 25px rgba(16, 185, 129, 0.08) !important;
        }
        
        @keyframes visitor-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes visitor-pulse {
          0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
          50% { transform: scale(1.05); opacity: 0.9; box-shadow: 0 0 15px 4px rgba(139, 92, 246, 0.25); }
        }

        @media (max-width: 768px) {
          .visitor-desktop-nav {
            display: none !important;
          }
          .visitor-mobile-nav {
            display: flex !important;
          }
          .visitor-main-container {
            padding: 12px 16px 90px 16px !important;
          }
          .visitor-floating-header {
            margin: 8px auto !important;
            width: calc(100% - 24px) !important;
            border-radius: 12px !important;
          }
          .visitor-header-name {
            display: none !important;
          }
        }
      `}</style>

    </div>
  );
}
